import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  ChevronLeft,
  CheckCircle,
  XCircle,
  Eye,
  Download,
} from 'lucide-react';
import api from '../../services/api';
import config from '../../config/config';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

async function openSuperAdminPdf(id, download = false) {
  const token = localStorage.getItem(config.TOKEN_STORAGE_KEY);
  const res = await fetch(`${config.API_BASE_URL}/superadmin/offer-letters/${id}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to load PDF');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  if (download) {
    const a = document.createElement('a');
    a.href = url;
    a.download = `offer-letter-${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}

const ListView = ({ status, onStatusChange, rows, loading, onRefresh }) => (
  <div className="space-y-6">
    <div className="flex flex-wrap items-start justify-between gap-4 rounded-3xl border border-slate-200 bg-gradient-to-r from-white to-slate-50/80 p-5 shadow-sm sm:p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Offer Letters</h1>
        <p className="mt-1 text-sm text-slate-500">Review and approve offer letters before they are sent to candidates.</p>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-600 shadow-sm hover:border-indigo-200 hover:text-indigo-700"
      >
        <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>

    <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white/90 p-2 shadow-sm">
      {STATUS_TABS.map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => onStatusChange(t.value)}
          className={`rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
            status === t.value
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>

    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white/90 shadow-sm">
      {loading ? (
        <LoadingSpinner size="lg" className="py-12" />
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-slate-400">No offer letters in this category</div>
      ) : (
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50/90">
            <tr>
              {['Candidate', 'Job title', 'Email', 'CTC (LPA)', 'Status', 'Submitted', ''].map((h) => (
                <th
                  key={h || 'act'}
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row._id} className="hover:bg-slate-50/70">
                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                  {row.salutation} {row.candidateName}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{row.position}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{row.email}</td>
                <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                  {row.computedCtcLpa != null ? `Rs ${Number(row.computedCtcLpa).toFixed(2)}` : '—'}
                </td>
                <td className="px-4 py-3 text-sm capitalize text-slate-600">{row.status}</td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500">
                  {row.updatedAt ? new Date(row.updatedAt).toLocaleString('en-IN') : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/superadmin/offer-letters/${row._id}`}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    Review
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

const DetailView = ({ letterId, onBack, onPendingRefresh }) => {
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/superadmin/offer-letters/${letterId}`);
      setLetter(res.data?.offerLetter);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load offer letter');
    } finally {
      setLoading(false);
    }
  }, [letterId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async () => {
    setActing(true);
    try {
      await api.put(`/superadmin/offer-letters/${letterId}/approve`);
      toast.success('Offer letter approved');
      onPendingRefresh?.();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Approve failed');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    setActing(true);
    try {
      await api.put(`/superadmin/offer-letters/${letterId}/reject`, {
        rejectionReason: rejectReason.trim() || undefined,
      });
      toast.success('Offer letter rejected');
      setRejectOpen(false);
      onPendingRefresh?.();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Reject failed');
    } finally {
      setActing(false);
    }
  };

  const handlePdf = async (download) => {
    try {
      await openSuperAdminPdf(letterId, download);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) return <LoadingSpinner size="lg" className="py-16" />;
  if (!letter) {
    return (
      <div className="text-center py-12 text-slate-500">
        Offer letter not found.
        <button type="button" onClick={onBack} className="ml-2 text-indigo-600">Back</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-indigo-700"
      >
        <ChevronLeft className="mr-1 h-4 w-4" /> Back to list
      </button>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              {letter.salutation} {letter.candidateName}
            </h1>
            <p className="mt-1 text-sm text-slate-500">{letter.position} · {letter.email}</p>
            <p className="mt-2 text-sm capitalize text-slate-600">Status: <span className="font-medium">{letter.status}</span></p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handlePdf(false)}
              className="inline-flex items-center rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Eye className="mr-1.5 h-4 w-4" /> View PDF
            </button>
            {letter.status === 'approved' && (
              <button
                type="button"
                onClick={() => handlePdf(true)}
                className="inline-flex items-center rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Download className="mr-1.5 h-4 w-4" /> Download
              </button>
            )}
            {letter.status === 'pending' && (
              <>
                <button
                  type="button"
                  disabled={acting}
                  onClick={handleApprove}
                  className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  <CheckCircle className="mr-1.5 h-4 w-4" /> Approve
                </button>
                <button
                  type="button"
                  disabled={acting}
                  onClick={() => setRejectOpen(true)}
                  className="inline-flex items-center rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <XCircle className="mr-1.5 h-4 w-4" /> Reject
                </button>
              </>
            )}
          </div>
        </div>

        <dl className="mt-6 grid gap-4 sm:grid-cols-2 text-sm">
          <div>
            <dt className="text-slate-500">Reporting manager</dt>
            <dd className="font-medium text-slate-900">{letter.reportingManager}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Grade</dt>
            <dd className="font-medium text-slate-900">{letter.gradeLabel || letter.grade}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Date of joining</dt>
            <dd className="font-medium text-slate-900">
              {letter.dateOfJoining
                ? new Date(letter.dateOfJoining).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">CTC (LPA)</dt>
            <dd className="font-medium text-slate-900">
              Rs {Number(letter.computedCtcLpa || 0).toFixed(2)}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-slate-500">Address</dt>
            <dd className="font-medium text-slate-900">
              {letter.addressLine1}
              {letter.addressLine2 && <br />}
              {letter.addressLine2}
              {letter.addressLine3 && <br />}
              {letter.addressLine3}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-slate-500">Job responsibilities</dt>
            <dd className="mt-1 space-y-1 text-slate-900">
              {(letter.jobResponsibilities || []).map((r, i) => (
                <p key={i}>{i + 1}. {r}</p>
              ))}
            </dd>
          </div>
          {letter.rejectionReason && (
            <div className="sm:col-span-2 rounded-lg bg-red-50 p-3 text-red-800">
              Rejection reason: {letter.rejectionReason}
            </div>
          )}
        </dl>
      </div>

      {rejectOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Reject offer letter</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Optional reason…"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRejectOpen(false)}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={acting}
                onClick={handleReject}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SuperAdminOfferLetters = ({ onSidebarPendingRefresh }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/superadmin/offer-letters?status=${encodeURIComponent(status)}`);
      setRows(res.data?.offerLetters || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (!id) load();
  }, [id, load]);

  if (id) {
    return (
      <DetailView
        letterId={id}
        onBack={() => navigate('/superadmin/offer-letters')}
        onPendingRefresh={onSidebarPendingRefresh}
      />
    );
  }

  return (
    <ListView
      status={status}
      onStatusChange={setStatus}
      rows={rows}
      loading={loading}
      onRefresh={load}
    />
  );
};

export default SuperAdminOfferLetters;
